/**
 * What has been done to this quotation, newest first.
 *
 * The screen already showed which round you are ON (`revision #N`) but nothing about how it got
 * there — so "what changed in revision 1?" had no answer anywhere in the app. Frappe records it;
 * `lib/quotationHistory.ts` reads it.
 *
 * Entries that bumped a revision are marked as such. Entries that did not — a convenience fee, a
 * price correction — still appear, because they changed what the customer would be charged and
 * pretending only revisions count would hide them.
 *
 * ---- one SEND, one row ----
 *
 * Not one row per save. Every keystroke that reaches the server writes a `Version`, and listing
 * all of them answers "what has been touched" when the question is "what did the customer get,
 * and how did it differ from last time". So a round is the unit: the send, and everything that
 * changed since the send before it.
 *
 * Sends come from `Email Queue`, because `Version` does not record them at all — measured
 * 2026-08-09, `send_quotation` writes outside Frappe's versioning. See `useQuotationSends.ts`.
 *
 * Changes made since the last send are shown SEPARATELY at the top rather than hidden. Dropping
 * them would let this panel imply the customer holds the current figures when they do not.
 *
 * Dates are RELATIVE ("2 hours ago") with the exact stamp on hover. Several saves land within
 * minutes of each other, so what a reader needs is the ORDER; the precise value stays reachable
 * because an audit trail that rounds away its own precision is not one.
 */

import { Badge } from '../../components/Badge'
import { Card } from '../../components/ui'
import { timeAgo, type ChangeKind, type HistoryChange, type SentRound } from '../../lib/quotationHistory'

/** Per-kind presentation. The parser decides the kind; this only dresses it. */
const KIND: Record<ChangeKind, { icon: string; tone: string }> = {
  added: { icon: '＋', tone: 'var(--ok)' },
  removed: { icon: '－', tone: 'var(--danger)' },
  qty: { icon: '#', tone: 'var(--violet)' },
  total: { icon: '∑', tone: 'var(--brand)' },
  status: { icon: '◆', tone: 'var(--teal)' },
  field: { icon: '·', tone: 'var(--muted)' },
}

function Change({ change }: { change: HistoryChange }) {
  const { icon, tone } = KIND[change.kind]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: 5,
        marginRight: 12,
        whiteSpace: 'nowrap',
      }}
    >
      <span aria-hidden style={{ color: tone, fontWeight: 700 }}>
        {icon}
      </span>
      <span style={{ color: 'var(--muted)' }}>{change.label}</span>
      {/*
        An addition has no "from" and a removal has no "to", so an arrow between two values would
        point at nothing. Each renders only the side it actually has.
      */}
      {change.from && change.to ? (
        <span className="mono">
          <span style={{ color: 'var(--muted)', textDecoration: 'line-through' }}>{change.from}</span>
          <span style={{ margin: '0 4px', color: 'var(--muted)' }}>→</span>
          <b>{change.to}</b>
        </span>
      ) : (
        <span className="mono">
          <b>{change.to || change.from}</b>
        </span>
      )}
    </span>
  )
}

export function RevisionHistory({
  rounds,
  unsent,
  loading,
  error,
}: {
  rounds: readonly SentRound[]
  unsent: readonly HistoryChange[]
  loading: boolean
  error: Error | null
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', margin: '18px 0 6px' }}>
        <div style={{ fontWeight: 800 }}>Sent to the customer</div>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          {rounds.length > 0
            ? `${rounds.length} time${rounds.length === 1 ? '' : 's'} · what changed between each`
            : 'what changed between each issue'}
        </span>
      </div>

      <Card padded={false}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 130 }}>Sent</th>
              <th style={{ width: 96 }}>Round</th>
              <th>Changed since the previous issue</th>
            </tr>
          </thead>
          <tbody>
            {/*
              Never hidden, and deliberately at the TOP. Edits made after the last send are the
              one thing a purchaser must not mistake for something the customer has — this is the
              row that stops someone quoting a figure down the phone that was never issued.
            */}
            {unsent.length > 0 ? (
              <tr>
                <td style={{ color: 'var(--warn)', fontWeight: 700, fontSize: 12 }}>Not sent yet</td>
                <td>
                  <Badge tone="warn">draft</Badge>
                </td>
                <td style={{ fontSize: 12.5, lineHeight: 1.9 }}>
                  {unsent.map((change, i) => (
                    <Change key={i} change={change} />
                  ))}
                </td>
              </tr>
            ) : null}

            {error ? (
              <tr>
                <td colSpan={3} style={{ color: 'var(--warn)' }}>
                  ⚠ Couldn't load the history. {error.message}
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={3} style={{ color: 'var(--muted)' }}>
                  Loading…
                </td>
              </tr>
            ) : rounds.length === 0 && unsent.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ color: 'var(--muted)' }}>
                  This quotation hasn't been sent to the customer yet.
                </td>
              </tr>
            ) : null}

            {rounds.map((round) => (
              <tr key={round.id}>
                <td style={{ color: 'var(--muted)', fontSize: 12 }} title={round.sentAt}>
                  {timeAgo(round.sentAt)}
                  {/* An attempted send is not a sent quotation — someone would chase a customer
                      who received nothing. */}
                  {!round.delivered ? (
                    <div style={{ color: 'var(--danger)', fontWeight: 700 }}>failed to send</div>
                  ) : null}
                </td>
                <td>
                  {round.revision !== null ? (
                    <Badge tone="info">Rev {round.revision}</Badge>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: 12 }}>first issue</span>
                  )}
                </td>
                <td style={{ fontSize: 12.5, lineHeight: 1.9 }}>
                  {round.changes.length > 0 ? (
                    round.changes.map((change, i) => <Change key={i} change={change} />)
                  ) : (
                    <span style={{ color: 'var(--muted)' }}>re-issued unchanged</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  )
}
